import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowRight, CheckCircle2 } from 'lucide-react';
import { auth, googleProvider, signInWithPopup } from '../firebase';

const EXAMPLES = [
  "AI resume reviewer",
  "Marketplace for college tutors",
  "Personal finance dashboard"
];

export default function Hero() {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  const navigate = useNavigate();

  // Typing effect for placeholder
  useEffect(() => {
    if (isFocused || inputValue) return;

    const currentExample = EXAMPLES[placeholderIndex];
    const typeSpeed = isDeleting ? 50 : 100;
    
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (displayText.length < currentExample.length) {
          setDisplayText(currentExample.slice(0, displayText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        if (displayText.length > 0) {
          setDisplayText(currentExample.slice(0, displayText.length - 1));
        } else {
          setIsDeleting(false);
          setPlaceholderIndex((prev) => (prev + 1) % EXAMPLES.length);
        }
      }
    }, typeSpeed);

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, placeholderIndex, isFocused, inputValue]);

  const handleGenerate = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!inputValue.trim() && !displayText) return;
    
    setIsGenerating(true);
    
    // Mock generation process
    setTimeout(async () => {
      if (!auth.currentUser) {
        try {
          await signInWithPopup(auth, googleProvider);
          navigate('/studio/build');
        } catch (error) {
          console.error("Login failed", error);
          setIsGenerating(false);
        }
      } else {
        navigate('/studio/build');
      }
    }, 2500);
  };

  return (
    <section className="min-h-screen relative flex flex-col items-center justify-center px-6 overflow-hidden bg-[#050505] text-white">
      {/* Background Glow */}
      <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none">
        <div className="w-[800px] h-[800px] bg-[radial-gradient(circle_at_center,_rgba(0,116,217,0.08)_0%,_transparent_60%)]" />
      </div>

      <div className="z-10 w-full max-w-3xl flex flex-col items-center text-center mt-16">
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-5xl md:text-7xl font-mono leading-[1.1] tracking-tighter uppercase mb-6"
        >
          Ship your next product in minutes.
        </motion.h1>

        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="text-lg md:text-xl font-light text-zinc-400 mb-12 font-mono tracking-tight"
        >
          From idea → landing page → live app → launch content.
        </motion.p>

        <motion.form 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          onSubmit={handleGenerate}
          className="w-full max-w-2xl relative mb-6"
        >
          <div className={`relative transition-all duration-300 ${isFocused ? 'scale-[1.02]' : 'scale-100'}`}>
            {/* Input Glow */}
            <div className={`absolute -inset-1 bg-gradient-to-r from-[#0074D9]/20 to-purple-500/20 rounded-2xl blur-xl transition-opacity duration-300 ${isFocused ? 'opacity-100' : 'opacity-0'}`} />
            
            <div className="relative flex flex-col sm:flex-row items-center bg-[#111111] border border-[#222222] rounded-2xl overflow-hidden focus-within:border-[#0074D9]/50 transition-colors p-2 gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder={isFocused ? "What do you want to build?" : displayText || "What do you want to build?"}
                disabled={isGenerating}
                className="w-full bg-transparent px-4 py-4 text-lg text-white placeholder:text-zinc-600 focus:outline-none disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={isGenerating || (!inputValue.trim() && !displayText)}
                className="w-full sm:w-auto px-8 py-4 bg-white text-black rounded-xl font-mono uppercase text-sm tracking-widest font-bold hover:bg-zinc-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group whitespace-nowrap"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    Generate my product
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.form>

        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          onClick={() => {
            setInputValue(EXAMPLES[0]);
            handleGenerate();
          }}
          disabled={isGenerating}
          className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition-colors mb-16"
        >
          See example
        </motion.button>

        {/* Preview Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="w-full max-w-lg bg-[#0A0A0A] border border-[#222222] rounded-2xl p-6 shadow-2xl relative overflow-hidden text-left"
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#0074D9]/50 to-transparent opacity-50" />
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isGenerating ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-800 text-zinc-500'}`}>
                <CheckCircle2 size={14} />
              </div>
              <span className={`font-mono text-sm ${isGenerating ? 'text-white' : 'text-zinc-500'}`}>Landing page generated</span>
            </div>
            
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isGenerating ? 'bg-emerald-500/20 text-emerald-400 delay-700 transition-colors' : 'bg-zinc-800 text-zinc-500'}`}>
                <CheckCircle2 size={14} />
              </div>
              <span className={`font-mono text-sm ${isGenerating ? 'text-white delay-700 transition-colors' : 'text-zinc-500'}`}>System architecture ready</span>
            </div>
            
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isGenerating ? 'bg-emerald-500/20 text-emerald-400 delay-1000 transition-colors' : 'bg-zinc-800 text-zinc-500'}`}>
                <CheckCircle2 size={14} />
              </div>
              <span className={`font-mono text-sm ${isGenerating ? 'text-white delay-1000 transition-colors' : 'text-zinc-500'}`}>Launch content created</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
